import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { PostersCarousel } from '../../components/Posters';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    GraduationCap, CreditCard, ClipboardList, Calendar, Bell,
    CheckCircle, AlertCircle, Plus, ArrowRight, Clock, Award, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentDashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [children, setChildren] = useState([]);
    const [homework, setHomework] = useState([]);
    const [parentProfile, setParentProfile] = useState(null);
    const [showPrevYears, setShowPrevYears] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const effectiveId = user?.linked_parent_id || user?.id;
                const [statsRes, childrenRes, homeworkRes, profileRes, settingsRes] = await Promise.all([
                    axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/students?status=active`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/homework`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/parents/${effectiveId}`, { withCredentials: true }).catch(() => ({ data: null })),
                    axios.get(`${API_URL}/api/settings`, { withCredentials: true }).catch(() => ({ data: null })),
                ]);
                setStats(statsRes.data);
                setChildren(childrenRes.data);
                setHomework(homeworkRes.data.slice(0, 5));
                setParentProfile(profileRes.data);
                setShowPrevYears(!!settingsRes.data?.show_previous_years_to_parents);
            } catch (error) {
                if (process.env.NODE_ENV === 'development') console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user?.id, user?.linked_parent_id]);

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    const fees = stats?.fees || {};

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="parent-dashboard">
                <PostersCarousel />
                {/* Welcome Section */}
                <div className="bg-gradient-to-r from-[#064E3B] to-[#047857] rounded-xl p-6 text-white">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold font-['Outfit'] mb-2">
                                Welcome, {user?.first_name}!
                            </h1>
                            <p className="text-white/80">
                                Parent Code: <span className="font-mono">{user?.reg_code}</span>
                            </p>
                        </div>
                        {stats?.waiting_list_count > 0 && (
                            <Badge className="bg-[#D97706] text-white w-fit">
                                <Clock className="h-3 w-3 mr-1" />
                                {stats.waiting_list_count} child(ren) in waiting list
                            </Badge>
                        )}
                    </div>
                </div>

                {/* Family Details */}
                {parentProfile && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="card-surface">
                            <CardHeader className="pb-2"><CardTitle className="text-sm font-['Outfit'] text-[#064E3B]">Guardian 1</CardTitle></CardHeader>
                            <CardContent className="space-y-1 text-sm">
                                <p className="font-medium text-[#1F2937]">{parentProfile.first_name} {parentProfile.last_name}</p>
                                <p className="text-[#4B5563]">{parentProfile.email}</p>
                                <p className="text-[#4B5563]">{parentProfile.mobile}</p>
                            </CardContent>
                        </Card>
                        <Card className="card-surface">
                            <CardHeader className="pb-2"><CardTitle className="text-sm font-['Outfit'] text-[#064E3B]">Guardian 2</CardTitle></CardHeader>
                            <CardContent className="space-y-1 text-sm">
                                {(parentProfile.guardian2_first_name || parentProfile.guardian2_last_name || parentProfile.guardian2_email || parentProfile.guardian2_mobile) ? (<>
                                    <p className="font-medium text-[#1F2937]">{parentProfile.guardian2_first_name || ''} {parentProfile.guardian2_last_name || ''}</p>
                                    <p className="text-[#4B5563]">{parentProfile.guardian2_email || '-'}</p>
                                    <p className="text-[#4B5563]">{parentProfile.guardian2_mobile || '-'}</p>
                                </>) : <p className="text-[#4B5563] py-2">No second guardian registered</p>}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">My Children</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.children_count || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#064E3B]/10">
                                    <GraduationCap className="h-6 w-6 text-[#064E3B]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Total Fees</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">€{fees.total_due?.toFixed(2) || '0.00'}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#D97706]/10">
                                    <CreditCard className="h-6 w-6 text-[#D97706]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Paid</p>
                                    <p className="text-3xl font-bold text-[#10B981] tabular-nums">€{fees.total_paid?.toFixed(2) || '0.00'}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#10B981]/10">
                                    <CheckCircle className="h-6 w-6 text-[#10B981]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Remaining</p>
                                    <p className={`text-3xl font-bold tabular-nums ${fees.remaining > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                                        €{fees.remaining?.toFixed(2) || '0.00'}
                                    </p>
                                </div>
                                <div className={`p-3 rounded-xl ${fees.remaining > 0 ? 'bg-[#EF4444]/10' : 'bg-[#10B981]/10'}`}>
                                    <AlertCircle className={`h-6 w-6 ${fees.remaining > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Children List */}
                    <Card className="card-surface">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="font-['Outfit']">My Children</CardTitle>
                                <CardDescription>Enrolled students</CardDescription>
                            </div>
                            <Link to="/parent/children">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </Link>
                        </CardHeader>
                        <CardContent>
                            {children.length > 0 ? (
                                <div className="space-y-3">
                                    {children.slice(0, 4).map((child) => (
                                        <div key={child.id} className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <div className="h-10 w-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-medium">
                                                    {child.first_name[0]}{child.last_name[0]}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[#1F2937]">
                                                        {child.first_name} {child.last_name}
                                                    </p>
                                                    <p className="text-xs text-[#4B5563] tabular-nums">
                                                        {child.reg_code}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge variant="outline" className="text-[#064E3B]">
                                                {child.status}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <GraduationCap className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                    <p className="text-[#4B5563] mb-4">No enrolled children yet</p>
                                    <Link to="/parent/children">
                                        <Button className="btn-primary" data-testid="add-child-button">
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add Child to Waiting List
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent Homework */}
                    <Card className="card-surface">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="font-['Outfit']">Recent Homework</CardTitle>
                                <CardDescription>Latest assignments</CardDescription>
                            </div>
                            <Link to="/parent/homework">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </Link>
                        </CardHeader>
                        <CardContent>
                            {homework.length > 0 ? (
                                <div className="space-y-3">
                                    {homework.map((hw) => (
                                        <div key={hw.id} className="p-3 bg-[#FAF9F6] rounded-lg">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="font-medium text-[#1F2937]">{hw.title}</p>
                                                    <p className="text-sm text-[#4B5563] line-clamp-1">{hw.description}</p>
                                                </div>
                                                {hw.due_date && (
                                                    <Badge variant="outline" className="shrink-0">
                                                        <Calendar className="h-3 w-3 mr-1" />
                                                        {new Date(hw.due_date).toLocaleDateString()}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <ClipboardList className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                    <p className="text-[#4B5563]">No homework assigned</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <Card className="card-surface">
                    <CardHeader>
                        <CardTitle className="font-['Outfit']">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Link to="/parent/payments" className="block">
                                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center" data-testid="quick-pay-fees">
                                    <CreditCard className="h-6 w-6 mb-2 text-[#064E3B]" />
                                    <span className="text-sm">Pay Fees</span>
                                </Button>
                            </Link>
                            <Link to="/parent/payments?tab=books" className="block">
                                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center" data-testid="quick-books">
                                    <BookOpen className="h-6 w-6 mb-2 text-[#B45309]" />
                                    <span className="text-sm">Books</span>
                                </Button>
                            </Link>
                            <Link to="/parent/attendance" className="block">
                                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center" data-testid="quick-attendance">
                                    <CheckCircle className="h-6 w-6 mb-2 text-[#064E3B]" />
                                    <span className="text-sm">Attendance</span>
                                </Button>
                            </Link>
                            <Link to="/parent/grades" className="block">
                                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center" data-testid="quick-grades">
                                    <Award className="h-6 w-6 mb-2 text-[#064E3B]" />
                                    <span className="text-sm">Grades</span>
                                </Button>
                            </Link>
                            <Link to="/parent/calendar" className="block">
                                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center" data-testid="quick-calendar">
                                    <Calendar className="h-6 w-6 mb-2 text-[#064E3B]" />
                                    <span className="text-sm">Calendar</span>
                                </Button>
                            </Link>
                            {showPrevYears && (
                                <Link to="/parent/previous-years" className="block">
                                    <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center" data-testid="quick-previous-years">
                                        <Clock className="h-6 w-6 mb-2 text-[#064E3B]" />
                                        <span className="text-sm">Previous Years</span>
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
};

export default ParentDashboard;
